import { useEffect, useRef } from "react";
import { Outlet, useNavigate, useParams } from "@tanstack/react-router";
import { ApiError } from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import { DocsSidebar } from "./DocsSidebar";

/**
 * /docs help center. Any signed-in user can read every article (role-agnostic);
 * anonymous visitors are bounced to /login with a `next` back to the page they
 * asked for. Two-pane layout mirrors the admin shell so it reads as native app
 * chrome.
 */
export function DocsShell() {
  const me = useMe();
  const navigate = useNavigate({ from: "/docs" });

  const isAuthError = me.error instanceof ApiError && me.error.status === 401;
  const isAnon = me.data === undefined ? false : me.data?.user == null;
  const showLogin = isAuthError || isAnon;

  const authGateMountPath = useRef(
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}` || "/docs"
      : "/docs",
  );
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (!showLogin) return;
    if (authRedirectFired.current) return;
    authRedirectFired.current = true;
    void navigate({ to: "/login", search: { next: authGateMountPath.current }, replace: true });
  }, [showLogin, navigate]);

  // Read the active slug (present only on /docs/$slug) so the sidebar can
  // highlight it. useParams with strict:false returns {} on the bare /docs route.
  const params = useParams({ strict: false }) as { slug?: string };

  if (showLogin) {
    return (
      <main className="min-h-screen bg-canvas" data-testid="docs-shell">
        <p className="p-6 text-sm text-ink-muted">Redirecting to sign in…</p>
      </main>
    );
  }

  return (
    <main data-testid="docs-shell" className="min-h-screen bg-canvas">
      <h1 className="sr-only">Tktic help</h1>
      <div className="flex min-h-screen flex-col items-stretch md:flex-row">
        <DocsSidebar activeSlug={params.slug} />
        <section data-testid="docs-content" className="min-w-0 flex-1 p-4 sm:p-6">
          <Outlet />
        </section>
      </div>
    </main>
  );
}
